import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { requestFlattenAll } from "../trading/actions";
import "./modal.css";

/**
 * A window-wide modal over the trading screen (Settings, the Rithmic login dialog): portalled to
 * the body so dock sashes never sit on top of it, centred over a backdrop. Escape, the close
 * button or a click on the backdrop close it and focus returns to the opener; Tab stays inside.
 * Ctrl+Shift+F still asks for Exit All (the shell ignores keys typed into inputs, so it is routed
 * here too); other workspace / palette shortcuts do not reach the trading screen behind it.
 * Nested dialogs and popovers inside keep their own keyboard behaviour.
 */
export function ModalDialog({
  title,
  onClose,
  className,
  closeLabel = "Return to trading",
  returnFocus,
  onCloseForExit,
  children,
}: {
  title: React.ReactNode;
  onClose: () => void;
  /**
   * Close for Ctrl+Shift+F (Exit All), when `onClose` may ask first (Settings with unsaved risk
   * changes): the exit request must never wait behind this dialog. Defaults to `onClose`.
   */
  onCloseForExit?: () => void;
  className?: string;
  closeLabel?: string;
  /** Focus target when the opener is gone (e.g. a menu item that closed). */
  returnFocus?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  const id = useId();
  const dialog = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const opener = useRef<Element | null>(null);
  const fallback = useRef(returnFocus);
  useEffect(() => {
    opener.current = document.activeElement;
    const fallbackSelector = fallback.current;
    closeButton.current?.focus();
    return () => {
      const target =
        opener.current instanceof HTMLElement && opener.current.isConnected
          ? opener.current
          : fallbackSelector
            ? document.querySelector<HTMLElement>(fallbackSelector)
            : null;
      target?.focus();
    };
  }, []);
  return createPortal(
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialog}
        className={className ? `modal-dialog ${className}` : "modal-dialog"}
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        onKeyDown={(event) => {
          if (!(event.target instanceof Element) || !dialog.current?.contains(event.target)) return;
          if (event.target.closest('[role="dialog"], [role="alertdialog"]') !== dialog.current) return;
          if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            onClose();
          } else if (event.key === "Tab") {
            // In tab order only (an inactive section tab has tabindex -1) and shown: not inside a
            // closed <details> (content-visibility: hidden) or a hidden subtree.
            const focusable = [
              ...dialog.current.querySelectorAll<HTMLElement>(
                'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], summary, [tabindex="0"]',
              ),
            ].filter(
              (el) =>
                el.tabIndex >= 0 &&
                el.getClientRects().length > 0 &&
                !el.closest("[hidden]") &&
                (typeof el.checkVisibility !== "function" ||
                  el.checkVisibility({ visibilityProperty: true })),
            );
            // Wrap to the far end; skip anything that will not take focus.
            const wrap = (from: number, step: 1 | -1): void => {
              for (let i = from; i >= 0 && i < focusable.length; i += step) {
                focusable[i]?.focus();
                if (document.activeElement === focusable[i]) return;
              }
            };
            const first = focusable[0];
            const last = focusable.at(-1);
            if (event.shiftKey && document.activeElement === first) {
              event.preventDefault();
              wrap(focusable.length - 1, -1);
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              wrap(0, 1);
            }
          } else if (
            event.ctrlKey &&
            event.shiftKey &&
            !event.altKey &&
            !event.metaKey &&
            event.code === "KeyF"
          ) {
            event.preventDefault();
            (onCloseForExit ?? onClose)();
            requestFlattenAll();
          }
          event.stopPropagation();
        }}
      >
        <header className="modal-dialog-head">
          <h2 id={id}>{title}</h2>
          <button ref={closeButton} className="btn btn-sm" type="button" onClick={onClose}>
            {closeLabel}
          </button>
        </header>
        {children}
      </div>
    </div>,
    document.body,
  );
}
